import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { FashionOutletType } from '..'
import axios from '../../../../config/axios'
import ActionsDropdown from './ActionsDropdown'
import eventBus from '../../../../utils/eventBus'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { buildFashionOrderColumns, fashionOrdersScreenKey, OrderType } from './fashionOrderColumns'

const ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`

const FashionOrdersTable: React.FC = () => {
  const { search, appliedFilters } = useOutletContext<FashionOutletType>()
  const [orders, setOrders] = useState<OrderType[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState(search || '')

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search || '')
    }, 400)

    return () => {
      clearTimeout(handler)
    }
  }, [search])

  const fetchData = useCallback(async (page: number, append: boolean = false) => {
    try {
      if (append) setIsFetchingNextPage(true)

      const size = 50
      const pageOneBased = page + 1
      const response = await axios.get(`${ORDERS_URL}/order/paginated`, {
        params: {
          page: pageOneBased,
          limit: size,
          onlyActive: 'true',
          search: debouncedSearch || undefined,
          filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined
        }
      })

      const result = response.data
      const ordersData = result.data || []
      const total = result.total || 0

      if (append) {
        setOrders(prev => [...prev, ...ordersData])
      } else {
        setOrders(ordersData)
        setTotalCount(total)
      }
    } catch (error) {
      console.error('Erro ao buscar os pedidos:', error)
    } finally {
      if (append) setIsFetchingNextPage(false)
    }
  }, [debouncedSearch, appliedFilters])

  useEffect(() => {
    fetchData(0)
  }, [fetchData])

  useEffect(() => {
    const handler = () => fetchData(0)
    eventBus.on('refreshFashionTable', handler)
    return () => eventBus.off('refreshFashionTable', handler)
  }, [fetchData])

  const hasMore = orders.length < totalCount

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage) {
      fetchData(Math.floor(orders.length / 50), true)
    }
  }, [isFetchingNextPage, fetchData, orders.length])

  const columns = useMemo(() => buildFashionOrderColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    fashionOrdersScreenKey,
    columns,
  )

  return (
    <StandardDataTable
      columns={columns}
      data={orders}
      columnsState={columnsState}
      onColumnsChange={handleColumnsChange}
      onRestoreColumns={handleRestoreColumns}
      totalCount={totalCount}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      isLoading={isFetchingNextPage}
      countLabel="pedidos"
      exportFileName="pedidos"
      renderActions={(order) => (
        <ActionsDropdown
          order={order}
          onDelete={() => setOrders((prev: OrderType[]) => prev.filter((o) => o.id !== order.id))}
          onUpdate={() => fetchData(0)}
        />
      )}
      emptyMessage="Nenhum pedido encontrado"
    />
  )
}

export default FashionOrdersTable
